import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  buildOrdersReportFilterChips,
  buildOrdersReportFilterOptions,
  countActiveOrdersReportFilters,
  EMPTY_ORDERS_REPORT_FILTERS,
  filterOrdersReportRows,
  mapOrderAvailableToReportRow,
  OrderService,
  OrdersAvailableReportRow,
  OrdersReportFilterChip,
  OrdersReportFilterOptions,
  OrdersReportFiltersState,
  removeOrdersReportFilter
} from 'src/entities/order';
import { OrderPdfDialogComponent } from 'src/features/service-order/order-pdf/ui/order-pdf-dialog/order-pdf-dialog.component';
import { buildOrderPdfDialogConfig } from 'src/features/service-order/order-pdf/ui/order-pdf-dialog/order-pdf-dialog.config';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-orders-report',
  templateUrl: './orders-report.component.html',
  styleUrls: ['./styles/orders-report.layout.css']
})
export class OrdersReportComponent implements OnInit, OnDestroy {
  /** Solo la primera página: bloquea tabla vacía. */
  isInitialLoading = false;
  /** Páginas siguientes en background. */
  isLoadingMore = false;
  loadedCount = 0;

  model: OrdersAvailableReportRow[] = [];
  filteredRows: OrdersAvailableReportRow[] = [];

  filters: OrdersReportFiltersState = { ...EMPTY_ORDERS_REPORT_FILTERS };
  filterOptions: OrdersReportFilterOptions = {
    years: [],
    providers: [],
    orderStatuses: []
  };
  activeFilterChips: OrdersReportFilterChip[] = [];

  private loadGeneration = 0;

  constructor(
    private readonly orderService: OrderService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    void this.loadOrders();
  }

  ngOnDestroy(): void {
    this.loadGeneration += 1;
  }

  get activeFilterCount(): number {
    return countActiveOrdersReportFilters(this.filters);
  }

  get isBusy(): boolean {
    return this.isInitialLoading || this.isLoadingMore;
  }

  async loadOrders(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.isInitialLoading = true;
    this.isLoadingMore = false;
    this.loadedCount = 0;
    this.model = [];
    this.filteredRows = [];
    this.filterOptions = buildOrdersReportFilterOptions([]);
    this.activeFilterChips = buildOrdersReportFilterChips(this.filters);

    try {
      await this.orderService.loadOrdersAvailablePageProgressively(
        (progress) => {
          if (generation !== this.loadGeneration) {
            return;
          }

          const mapped = progress.items.map(mapOrderAvailableToReportRow);
          this.model = [...this.model, ...mapped];
          this.loadedCount = progress.loadedCount;
          this.filterOptions = buildOrdersReportFilterOptions(this.model);
          this.applyFilters();

          if (progress.pageIndex === 0) {
            this.isInitialLoading = false;
          }
          this.isLoadingMore = progress.hasMore;
        },
        {
          isCancelled: () => generation !== this.loadGeneration
        }
      );

      if (generation === this.loadGeneration) {
        this.isInitialLoading = false;
        this.isLoadingMore = false;
      }
    } catch (error) {
      if (generation !== this.loadGeneration) {
        return;
      }
      console.error('Error cargando getOrdersAvailablePage:', error);
      this.model = [];
      this.filteredRows = [];
      this.loadedCount = 0;
      this.activeFilterChips = buildOrdersReportFilterChips(this.filters);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo cargar el reporte de OS',
        text: 'Intenta actualizar. Si el problema continúa, reinicia con lastKey null.',
        confirmButtonText: 'Entendido'
      });
    } finally {
      if (generation === this.loadGeneration) {
        this.isInitialLoading = false;
        this.isLoadingMore = false;
      }
    }
  }

  onFiltersChange(next: OrdersReportFiltersState): void {
    this.filters = next;
    this.applyFilters();
  }

  onClearFilters(): void {
    this.filters = { ...EMPTY_ORDERS_REPORT_FILTERS };
    this.applyFilters();
  }

  onRemoveFilter(chipId: OrdersReportFilterChip['id']): void {
    this.filters = removeOrdersReportFilter(this.filters, chipId);
    this.applyFilters();
  }

  onOpenPdf(row: OrdersAvailableReportRow): void {
    if (!row?.orderId) {
      return;
    }

    this.dialog.open(
      OrderPdfDialogComponent,
      buildOrderPdfDialogConfig({
        orderId: row.orderId,
        orderNumber: row.orderNumber
      })
    );
  }

  private applyFilters(): void {
    this.filteredRows = filterOrdersReportRows(this.model, this.filters);
    this.activeFilterChips = buildOrdersReportFilterChips(this.filters);
  }
}
